//@flow

import {getFileInformationUrl, getImageUrl} from '../../utils/image_provider';
import type {FileInfoResponse} from '../../entities';

export function getOriginalFileUrl(fileId: string): Promise<string> {
  return fetch(getFileInformationUrl(fileId))
    .then(res => res.json())
    .then(data => {
      if (data && data.url) {
        return data.url;
      }
      throw new Error('Original file URL not found');
    })
    .catch(err => {
      console.log(err);
      return '';
    });
}

export function getFileInfo(fileId: string): Promise<FileInfoResponse | null> {
  return fetch(getFileInformationUrl(fileId))
    .then(res => res.json())
    .then((data: FileInfoResponse) => {
      if (data && data.id) {
        return data;
      }
      return null;
    })
    .catch(err => {
      console.log(err);
      return null;
    });
}

export function getResourceType(file: File | Blob): string {
  const {type, name} = file;

  if (type.startsWith('image/')) return 'IMAGE';
  if (type.startsWith('video/')) return 'VIDEO';
  if (type.startsWith('audio/')) return 'AUDIO';
  if (type === 'application/pdf') return 'PDF';
  if (type === 'text/html') return 'HTML';
  if (
    type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    (name && name.endsWith('.xlsx'))
  )
    return 'XLSX';

  return 'PDF';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

async function urlToDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Request to "${url}" failed: ${res.status}`);
  return blobToDataUrl(await res.blob());
}

/**
 * Resolves a file as a `data:` URL so it can be rendered by libraries that
 * re-fetch the image themselves (html-to-image), where a cross origin URL
 * would be blocked by CORS and end up as a blank area in the output.
 *
 * Tries the storage URL exposed by the file info endpoint first and falls back
 * to the same origin API route. If both fail, the plain URL is returned so the
 * image still renders in the DOM.
 */
export async function getFileDataUrl(fileId: ?string): Promise<string> {
  const apiUrl = getImageUrl(fileId);

  if (!fileId) return apiUrl;

  const directUrl = await getOriginalFileUrl(fileId);

  for (const url of [directUrl, apiUrl]) {
    if (!url) continue;
    try {
      return await urlToDataUrl(url);
    } catch (err) {
      console.log(err);
    }
  }

  return apiUrl;
}
